import React from 'react'
import useTrans from '@hooks/useTrans'
import { cn } from '@utils/safeTs'
import { AlertTriangle, Bell, Loader, MailOpen } from '@components/icons'
import styles from './MyAccountNotificheBox.module.sass'
import MyAccountDashboardBox from '../MyAccountDashboardBox/MyAccountDashboardBox'
import { Alert } from '@components/atoms'
import Link from 'next/link'

export type MyAccountNotificheBoxVariant = 'notifiche' | 'alert'

export type MyAccountNotificheBoxItem = {
  id: string
  value: number
  label: string
  labelSingolare: string
  url?: string
  color?: 'black'
}

export interface MyAccountNotificheBoxProps {
  variant?: MyAccountNotificheBoxVariant
  items: MyAccountNotificheBoxItem[]
  error?: string
  loading?: boolean
  className?: string
  onMarkAllRead?: () => void
  loadingMarkAllRead?: boolean
}

const variantMap: Record<MyAccountNotificheBoxVariant, { icon: React.ReactNode, title: string, emptyMessage: string }> = {
  notifiche: { icon: <Bell />, title: "Notifiche", emptyMessage: "Nessuna notifica" },
  alert: { icon: <AlertTriangle className={styles.icon} />, title: "Alert", emptyMessage: "Nessun alert" },
}

const MyAccountNotificheBox = ({
  variant = 'notifiche',
  items,
  error,
  loading = false,
  className,
  onMarkAllRead,
  loadingMarkAllRead = false,
}: MyAccountNotificheBoxProps) => {

  const t = useTrans()
  const showAction = variant === 'notifiche' && typeof onMarkAllRead === 'function'
  const badgeValue = items?.reduce((count, x) => count += x.value, 0) || 0

  return (
    <MyAccountDashboardBox
      title={variantMap[variant].title}
      icon={variantMap[variant].icon}
      badgeValue={badgeValue}
      variant={variant}
      loading={loading}
      className={cn(styles.root, styles[`root--${variant}`], className)}
    >
      <div className={styles.body}>
        {error ? (
          <Alert variant='danger' full styleVariant='basic' placeholder={error} className={styles.error} />
        ) : (
          items?.length ? (
            <>
              <ul className={styles.list}>
                {items.map((item) => (
                  <li key={`${item.label}-${item.value}`} className={cn(styles.item, item.value ? item.color ? styles[`item--${item.color}`] : '' : styles['item--zero'])}>
                    {item.url ? (
                      <Link href={item.url}>
                        <span className={styles.value}>{item.value}</span>{' '}
                        <span className={styles.label}>{t(item.value === 1 ? item.labelSingolare : item.label)}</span>
                      </Link>
                    ) : (
                      <>
                        <span className={styles.value}>{item.value}</span>{' '}
                        <span className={styles.label}>{t(item.value === 1 ? item.labelSingolare : item.label)}</span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              {showAction ? (
                <div className={styles.footer}>
                  <button type="button" className={cn(styles.action, loadingMarkAllRead ? styles['action--loading'] : '')} onClick={onMarkAllRead} disabled={loadingMarkAllRead || items.filter(x => x.value > 0)?.length === 0}>
                    <Loader aria-hidden className={styles.actionLoader} />
                    <MailOpen aria-hidden className={styles.actionIcon} />
                    <span>{t('Segna tutto come già letto')}</span>
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <div className={styles.empty}>
              {t(variantMap[variant].emptyMessage)}
            </div>
          )
        )}
      </div>
    </MyAccountDashboardBox>
  )
}

export default MyAccountNotificheBox
